import { useMemo } from 'react'
import {
    type APIWorkOrder,
    type APIWorkOrderSegment,
    type APIWorkOrderListParams,
    type APIOrganization,
    type PaginatedRequestQueryParams,
} from '@visorpro/client'
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Updater,
    type MRT_PaginationState,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
} from 'material-react-table'
import _ from 'lodash'
import { useAdminMaterialReactTable } from './table/mui-table'
import { useMultiValueColumnDef } from './table/column-definition'
import { Chip } from '@mui/material'
import { Link } from 'react-router-dom'
import type { DictionaryArray } from '../types'
import type { DateTime } from 'luxon'
import { formatDate } from '../util/format'
import type { SearchableSelectOption } from './searchable-select'

export interface WorkOrdersTableProps {
    workOrders: APIWorkOrder[]
    organizations: DictionaryArray<APIOrganization>
    isLoading?: boolean
    isSaving?: boolean
    rowCount?: number
    pagination?: PaginatedRequestQueryParams
    filters?: Omit<APIWorkOrderListParams, 'sort_by'>
    sorting?: APIWorkOrderListParams['sort_by']
    setPagination?: (pagination: PaginatedRequestQueryParams) => void
    setFilters?: (filters: Omit<APIWorkOrderListParams, 'sort_by'>) => void
    setSorting?: (sorting: string[]) => void
    isPrimaryBackgroundColor?: boolean
}

export const WorkOrdersTable = ({
    workOrders: data,
    organizations,
    rowCount,
    pagination,
    filters,
    sorting,
    setPagination,
    setFilters,
    setSorting,
    isLoading,
    isSaving,
    isPrimaryBackgroundColor,
}: WorkOrdersTableProps) => {
    const paginationState = useMemo(() => {
        if (pagination === undefined) return undefined

        return {
            pageIndex:
                pagination.offset && pagination.limit
                    ? pagination.offset / pagination.limit
                    : 0,
            pageSize: pagination.limit || 1000,
        }
    }, [pagination])

    const onPaginationChange = (
        pagination: MRT_Updater<MRT_PaginationState>,
    ) => {
        if (typeof pagination === 'function') {
            const newPagination = pagination(table.getState().pagination)
            setPagination?.({
                offset: newPagination.pageIndex * newPagination.pageSize,
                limit: newPagination.pageSize,
            })
        } else {
            setPagination?.({
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
            })
        }
    }

    const getOrganizationOptions = (
        organizations: APIOrganization[],
    ): SearchableSelectOption[] => {
        return organizations.map((organization) => ({
            value: organization.id,
            label: organization.name,
        }))
    }

    const organizationColumnDefs = useMultiValueColumnDef<APIWorkOrder, APIOrganization>({
        fieldName: 'Organizations',
        optionsData: organizations,
        getOptions: getOrganizationOptions,
    })

    const columnFiltersState = useMemo(() => {
        if (filters === undefined) return undefined

        const columnFilters: MRT_ColumnFiltersState = []
        Object.entries(filters).forEach(([key, value]) => {
            columnFilters.push({ id: key, value })
        })

        return columnFilters
    }, [filters])

    const onColumnFiltersChange = (
        filters: MRT_Updater<MRT_ColumnFiltersState>,
    ) => {
        const oldFilters = table.getState().columnFilters
        let newFilters: MRT_ColumnFiltersState

        if (typeof filters === 'function') {
            newFilters = filters(oldFilters)
        } else {
            newFilters = filters
        }

        if (_.isEqual(newFilters, oldFilters)) return

        const filtersBody: Record<string, any> = {}
        newFilters.forEach((filter) => {
            filtersBody[filter.id] = filter.value
        })

        setFilters?.(filtersBody)
        table.firstPage()
    }

    const sortingState = useMemo(() => {
        if (sorting === undefined) return []

        return sorting.map((sort) => {
            if (sort.startsWith('-')) {
                return { id: sort.slice(1), desc: true }
            } else {
                return { id: sort, desc: false }
            }
        })
    }, [sorting])

    const onSortingChange = (sorting: MRT_Updater<MRT_SortingState>) => {
        let newSorting: MRT_SortingState
        const oldSorting = table.getState().sorting

        if (typeof sorting === 'function') {
            newSorting = sorting(oldSorting)
        } else {
            newSorting = sorting
        }

        if (_.isEqual(newSorting, oldSorting)) return

        const sortingBody = newSorting.map((sort) => {
            return sort.desc ? `-${sort.id}` : sort.id
        })
        setSorting?.(sortingBody)
    }

    const columns = useMemo<MRT_ColumnDef<APIWorkOrder>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                enableClickToCopy: true,
                enableColumnFilter: true,
            },
            {
                accessorKey: 'work_order_number',
                header: 'Work Order Number',
                enableClickToCopy: true,
                enableColumnFilter: true,
            },
            {
                accessorKey: 'title',
                header: 'Title',
                enableColumnFilter: true,
            },
            {
                accessorKey: 'kind',
                header: 'Kind',
                enableColumnFilter: true,
            },
            {
                id: 'organization_ids',
                accessorFn: (row) => row.organization_id,
                header: 'Organization',
                Cell: ({ cell }) => {
                    const organizationId = cell.getValue<string>()
                    const organization = organizations.get(organizationId)

                    return (
                        <Link
                            className="text-link"
                            to={`/organizations/${organizationId}`}>
                            <Chip
                                label={organization?.name}
                                size="small"
                                clickable
                            />
                        </Link>
                    )
                },
                filterFn: 'objectId',
                enableSorting: false,
                ...organizationColumnDefs,
            },
            {
                accessorKey: 'location',
                header: 'Location',
            },
            {
                accessorKey: 'created_at',
                header: 'Created At',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
            },
            {
                accessorKey: 'updated_at',
                header: 'Updated At',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
            },
            {
                accessorKey: 'invoiced_at',
                header: 'Invoiced At',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
            },
            {
                accessorKey: 'invoice_last_updated_at',
                header: 'Invoice Last Updated At',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
            },
            {
                accessorKey: 'external_id',
                header: 'External ID',
                enableClickToCopy: true,
                enableColumnFilter: true,
            },
            {
                accessorKey: 'segments',
                header: 'Segments',
                Cell: ({ cell }) =>
                    cell.getValue<APIWorkOrderSegment[]>().length,
            },
        ],
        [organizationColumnDefs, organizations],
    )

    const table = useAdminMaterialReactTable(
        {
            columns,
            data,
            rowCount,
            initialState: {
                columnVisibility: {
                    sha512_hash: false,
                    updated_at: false,
                },
                columnPinning: {
                    left: ['id', 'work_order_number'],
                },
            },
            defaultColumn: {
                minSize: 80,
                size: 200,
                enableSorting: true,
            },
            enableSorting: true,
            enableRowSelection: false,
            getRowId: (doc) => doc.id,
            ...(setPagination && {
                manualPagination: true,
                onPaginationChange,
            }),
            state: {
                isLoading,
                showProgressBars: isSaving,
                ...(pagination &&
                    paginationState && { pagination: paginationState }),
                ...(columnFiltersState && {
                    columnFilters: columnFiltersState,
                }),
                ...(!!sorting && !!sortingState && { sorting: sortingState }),
            },
            ...(!!setFilters && {
                manualFiltering: true,
                onColumnFiltersChange,
            }),
            ...(!!setSorting && {
                manualSorting: true,
                onSortingChange: onSortingChange,
            }),
            isPrimaryBackgroundColor,
        },
        'work-orders-table',
    )

    return <MaterialReactTable table={table} />
}
